/**
 * Importeur Excel/CSV pour saisie de masse (salariés, écritures, articles).
 */
import { chargerExcelJs, versArrayBuffer } from '@infra/export/ExcelJsLoader';
import { lireClasseurSecurise, type FeuilleLue } from './xlsx/SafeXlsxReader';

export interface ImportResult<T> {
  data: T[];
  errors: { ligne: number; message: string }[];
}

export class ExcelImporter {
  /** Parse un CSV avec en-tête. Retourne les lignes typées. */
  static parseCSV<T>(
    contenu: string,
    parserLigne: (row: Record<string, string>, ligne: number) => T | null,
  ): ImportResult<T> {
    const result: ImportResult<T> = { data: [], errors: [] };
    const lignes = contenu.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lignes.length === 0) return result;
    const headers = this.parseLigne(lignes[0]!);
    for (let i = 1; i < lignes.length; i++) {
      const cellules = this.parseLigne(lignes[i]!);
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        row[h] = cellules[idx] ?? '';
      });
      try {
        const parsed = parserLigne(row, i + 1);
        if (parsed) result.data.push(parsed);
      } catch (err) {
        result.errors.push({
          ligne: i + 1,
          message: String(err instanceof Error ? err.message : err),
        });
      }
    }
    return result;
  }

  /**
   * Convertit la première feuille d’un classeur Excel en lignes objet
   * (clés = en-têtes de la ligne 1).
   */
  static sheetToObjects(buffer: ArrayBuffer): Record<string, string>[] {
    const sheets = this.workbookToSheetObjects(buffer);
    const first = Object.values(sheets)[0];
    return first ?? [];
  }

  /**
   * Toutes les feuilles visibles nommées → lignes objet (clés = en-têtes de la 1re ligne
   * renseignée). Lecture bornée d’un fichier non fiable (voir `xlsx/SafeXlsxReader`) : lève
   * `XlsxImportError` si le fichier dépasse les limites ou contient macros/liens externes.
   */
  static workbookToSheetObjects(buffer: ArrayBuffer): Record<string, Record<string, string>[]> {
    const classeur = lireClasseurSecurise(buffer);
    const out: Record<string, Record<string, string>[]> = {};
    for (const feuille of classeur.feuilles) {
      out[feuille.nom] = this.feuilleVersObjets(feuille);
    }
    return out;
  }

  private static feuilleVersObjets(feuille: FeuilleLue): Record<string, string>[] {
    const cellules = feuille.cellules;
    if (cellules.length === 0) return [];
    let ligneMin = Infinity;
    let colMin = Infinity;
    let ligneMax = 0;
    let colMax = 0;
    const lignes = new Map<number, Map<number, string | null>>();
    for (const c of cellules) {
      ligneMin = Math.min(ligneMin, c.ligne);
      ligneMax = Math.max(ligneMax, c.ligne);
      colMin = Math.min(colMin, c.colonne);
      colMax = Math.max(colMax, c.colonne);
      let l = lignes.get(c.ligne);
      if (!l) {
        l = new Map();
        lignes.set(c.ligne, l);
      }
      l.set(c.colonne, c.texte);
    }
    // En-têtes : 1re ligne ; absent = __EMPTY, doublon = suffixe _1, _2…
    const entetes: string[] = [];
    const compte = new Map<string, number>();
    const premiere = lignes.get(ligneMin);
    for (let col = colMin; col <= colMax; col++) {
      const brut = premiere?.get(col);
      // « __proto__ » ne doit jamais devenir une clé d’objet (pollution de prototype).
      const base =
        brut === undefined || brut === null ? '__EMPTY' : brut === '__proto__' ? '__proto_' : brut;
      let cle = base;
      let n = compte.get(base) ?? 0;
      if (n === 0) {
        compte.set(base, 1);
      } else {
        do {
          cle = `${base}_${n++}`;
        } while (compte.has(cle));
        compte.set(base, n);
        compte.set(cle, 1);
      }
      entetes.push(cle);
    }
    const rows: Record<string, string>[] = [];
    for (let r = ligneMin + 1; r <= ligneMax; r++) {
      const l = lignes.get(r);
      if (!l || [...l.values()].every((t) => t === null)) continue;
      const row: Record<string, string> = {};
      entetes.forEach((cle, i) => {
        row[cle] = (l.get(colMin + i) ?? '').trim();
      });
      rows.push(row);
    }
    return rows;
  }

  /**
   * Construit un classeur .xlsx (ArrayBuffer) à partir de feuilles { nom → lignes }.
   * Les colonnes sont l’union des clés, dans l’ordre d’apparition ; en-têtes en ligne 1.
   */
  static async buildWorkbook(
    sheets: Record<string, Array<Record<string, string | number>>>,
  ): Promise<ArrayBuffer> {
    const { Workbook } = await chargerExcelJs();
    const workbook = new Workbook();
    for (const [name, rows] of Object.entries(sheets)) {
      const ws = workbook.addWorksheet(name.slice(0, 31));
      const colonnes: string[] = [];
      for (const row of rows) {
        for (const k of Object.keys(row)) if (!colonnes.includes(k)) colonnes.push(k);
      }
      if (colonnes.length === 0) continue;
      ws.addRow(colonnes);
      for (const row of rows) {
        ws.addRow(colonnes.map((k) => row[k] ?? null));
      }
    }
    return versArrayBuffer(await workbook.xlsx.writeBuffer());
  }

  private static parseLigne(ligne: string): string[] {
    const cellules: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < ligne.length; i++) {
      const c = ligne[i];
      if (inQuotes) {
        if (c === '"' && ligne[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (c === '"') inQuotes = false;
        else cur += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ',' || c === ';') {
          cellules.push(cur.trim());
          cur = '';
        } else cur += c;
      }
    }
    cellules.push(cur.trim());
    return cellules;
  }
}
